import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { log } from '../lib/logger';
import { Button } from '../components/ui/button';
import {
  ShoppingCart,
  FileText,
  Tag,
  Rocket,
  Layers,
  Clock,
  Mail,
  Phone,
  Trash2,
  RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import { setActiveDraftOrderId } from '../lib/activeDraftOrder';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Relative-time hints for draft cards (iter114s.103). Drafts are
// auto-pruned server-side after DRAFT_RETENTION_DAYS (30) — surface a
// countdown once a draft is within a week of deletion.
const DRAFT_RETENTION_DAYS = 30;
const daysSince = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
const timeAgo = (iso) => {
  const days = daysSince(iso);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
};
const draftExpiryHint = (iso) => {
  const left = DRAFT_RETENTION_DAYS - daysSince(iso);
  return left <= 7 ? ` · auto-deletes in ${Math.max(left, 0)}d` : '';
};

/**
 * Customer home — single landing page after login replacing the product-
 * centric Splash. Surfaces:
 *   - Draft quotes (from sessionStorage + DB drafts)
 *   - Recent placed orders (top 3)
 *   - Saved templates
 *   - Primary CTAs (start new quote, browse catalog)
 *   - Account Manager card for Evergreen-tier users
 */
const CustomerDashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading, isEvergreenTier } = useAuth();
  const [orders, setOrders] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true, state: { from: { pathname: '/dashboard' } } });
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let alive = true;
    (async () => {
      try {
        const [ordersResp, templatesResp] = await Promise.all([
          axios.get(`${API_URL}/api/enhanced-orders/my-orders`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/templates/my-templates`).catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        const all = Array.isArray(ordersResp.data) ? ordersResp.data : [];
        setOrders(all.filter((o) => o.status !== 'draft').slice(0, 3));
        setDrafts(all.filter((o) => o.status === 'draft').slice(0, 3));
        setTemplates(Array.isArray(templatesResp.data) ? templatesResp.data.slice(0, 3) : []);
      } catch (err) {
        log.error('Failed to load dashboard', err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [isAuthenticated]);

  const spinUpTemplate = async (templateId) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/templates/${templateId}/spin-up`,
        {},
      );
      navigate('/order-summary', { state: { orderId: data.order_id } });
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to spin up template');
    }
  };

  const resumeDraft = (orderId) => {
    navigate('/order-summary', { state: { orderId } });
  };

  // 1-click reorder (iter114s.102): clone the past order — items,
  // saved artwork references and shipping info — into a fresh DRAFT
  // and drop the customer on the order summary to review & submit.
  const [reorderingId, setReorderingId] = useState(null);
  const reorderOrder = async (e, orderId) => {
    e.stopPropagation();
    if (reorderingId) return;
    setReorderingId(orderId);
    try {
      const resp = await axios.post(
        `${API_URL}/api/enhanced-orders/${orderId}/reorder?as_draft=true`
      );
      const newId = resp.data?.order_id;
      setActiveDraftOrderId(newId);
      toast.success(`Reorder ${resp.data?.order_number || ''} created — review and submit when ready.`);
      navigate('/order-summary', { state: { orderId: newId } });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create the reorder. Please try again.');
    } finally {
      setReorderingId(null);
    }
  };

  /**
   * iter114f: customer-driven draft cleanup. Confirms with the user
   * before hitting DELETE so a stray click doesn't nuke a half-built
   * quote, and optimistically removes the card from local state on
   * success — no full reload needed.
   */
  const deleteDraft = async (e, orderId) => {
    // Stop the click from bubbling up to the card's `onClick` (which
    // would resume-into the order we're about to delete).
    e.stopPropagation();
    e.preventDefault();
    const ok = window.confirm(
      'Delete this draft? This cannot be undone — any quoted items, sizes, and pricing on this draft will be discarded.',
    );
    if (!ok) return;
    try {
      await axios.delete(`${API_URL}/api/enhanced-orders/${orderId}`);
      setDrafts((prev) => prev.filter((d) => d.id !== orderId));
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to delete draft.';
      log.error('Draft delete failed:', err);
      alert(detail);
    }
  };

  const acct = user?.account_manager;

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white" data-testid="customer-dashboard">
      <header className="bg-gradient-to-r from-[#2F5D3A] to-[#4A7C59] text-white py-10 px-6 shadow-md">
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-green-100 text-sm uppercase tracking-widest">
              {isEvergreenTier ? 'Evergreen Strategic Partner' : 'Welcome back'}
            </p>
            <h1 className="text-3xl font-bold" data-testid="dashboard-greeting">
              Hello, {user?.first_name || 'there'} 👋
            </h1>
            <p className="text-green-100 mt-1">
              {isEvergreenTier
                ? 'Your dashboard — Strategic-Partner pricing is already applied to every quote.'
                : 'Your dashboard — jump back into a draft or start something new.'}
            </p>
          </div>
          <div className="flex gap-3">
            <Link to="/quote">
              <Button className="bg-[#6FBF73] hover:bg-[#4A7C59] text-white" data-testid="dashboard-new-quote-btn">
                <Rocket className="w-4 h-4 mr-2" /> Start New Quote
              </Button>
            </Link>
            <Link to="/pricing-catalog">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#2F5D3A]" data-testid="dashboard-catalog-btn">
                <Tag className="w-4 h-4 mr-2" /> Pricing Catalog
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10 space-y-10">
        {/* Account Manager card (only for Evergreen + users with a rep assigned) */}
        {isEvergreenTier && acct && (
          <section
            className="bg-white rounded-xl shadow-sm border border-[#6FBF73]/30 p-6 flex items-center gap-6"
            data-testid="dashboard-account-manager"
          >
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#6FBF73] to-[#2F5D3A] text-white flex items-center justify-center font-bold text-2xl shrink-0">
              {acct.avatar_url ? (
                <img src={acct.avatar_url} alt={acct.name} className="w-full h-full object-cover rounded-full" />
              ) : (
                acct.name?.[0]?.toUpperCase() || 'A'
              )}
            </div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-widest text-[#4A7C59] font-semibold">
                {acct.title || 'Your Account Manager'}
              </p>
              <p className="text-xl font-bold text-gray-800" data-testid="account-manager-name">{acct.name}</p>
              <div className="flex flex-wrap gap-3 mt-2 text-sm">
                <a href={`mailto:${acct.email}`} className="flex items-center gap-1 text-[#4A7C59] hover:underline">
                  <Mail className="w-4 h-4" /> {acct.email}
                </a>
                {acct.phone && (
                  <a href={`tel:${acct.phone}`} className="flex items-center gap-1 text-[#4A7C59] hover:underline">
                    <Phone className="w-4 h-4" /> {acct.phone}
                  </a>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Drafts */}
        <DashboardSection
          title="Draft Quotes"
          icon={<Clock className="w-5 h-5 text-[#4A7C59]" />}
          emptyLabel="No drafts yet — start a new quote to save one."
          emptyCta={{ label: 'Start new quote', to: '/quote' }}
          items={drafts}
          loading={loading}
          testId="dashboard-drafts"
        >
          {drafts.map((d) => (
            <DashboardCard
              key={d.id}
              title={d.order_number}
              subtitle={`${(d.items || []).length} item${(d.items || []).length === 1 ? '' : 's'} · ${d.currency || 'CAD'} $${(d.total_price || 0).toFixed(2)}`}
              meta={`Created ${timeAgo(d.created_at)}${draftExpiryHint(d.created_at)}`}
              cta="Resume"
              onClick={() => resumeDraft(d.id)}
              testId={`dashboard-draft-${d.id}`}
              onDelete={(e) => deleteDraft(e, d.id)}
            />
          ))}
        </DashboardSection>

        {/* Recent orders */}
        <DashboardSection
          title="Recent Orders"
          icon={<ShoppingCart className="w-5 h-5 text-[#4A7C59]" />}
          emptyLabel="No orders placed yet."
          items={orders}
          loading={loading}
          testId="dashboard-recent-orders"
          viewAllLink="/po-history"
        >
          {orders.map((o) => (
            <DashboardCard
              key={o.id}
              title={o.order_number}
              subtitle={`${(o.items || []).length} item${(o.items || []).length === 1 ? '' : 's'} · ${o.currency || 'CAD'} $${(o.total_price || 0).toFixed(2)}`}
              meta={o.status?.replace(/_/g, ' ').toUpperCase()}
              shipDate={o.estimated_ship_date}
              cta="View"
              onClick={() => navigate('/po-history')}
              testId={`dashboard-order-${o.id}`}
              secondaryCta={reorderingId === o.id ? 'Reordering…' : 'Reorder'}
              secondaryIcon={<RotateCcw className="w-3.5 h-3.5" />}
              onSecondary={(e) => reorderOrder(e, o.id)}
            />
          ))}
        </DashboardSection>

        {/* Templates */}
        <DashboardSection
          title="Saved Templates"
          icon={<Layers className="w-5 h-5 text-[#4A7C59]" />}
          emptyLabel="No templates saved yet. Save a cart from the order-summary page to spin up draft orders in one click."
          items={templates}
          loading={loading}
          testId="dashboard-templates"
          viewAllLink="/po-history#templates"
        >
          {templates.map((t) => (
            <DashboardCard
              key={t.id}
              title={t.name}
              subtitle={`${t.item_count} item${t.item_count === 1 ? '' : 's'} · ${t.currency} $${(t.total_price || 0).toFixed(2)}`}
              meta={new Date(t.created_at).toLocaleDateString()}
              cta="Start from template"
              onClick={() => spinUpTemplate(t.id)}
              testId={`dashboard-template-${t.id}`}
            />
          ))}
        </DashboardSection>
      </main>
    </div>
  );
};

const DashboardSection = ({ title, icon, emptyLabel, emptyCta, items, loading, children, testId, viewAllLink }) => (
  <section data-testid={testId}>
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-2xl font-bold text-[#2F5D3A] flex items-center gap-2">
        {icon} {title}
      </h2>
      {viewAllLink && (
        <Link to={viewAllLink} className="text-sm text-[#4A7C59] hover:underline font-medium">
          View all →
        </Link>
      )}
    </div>
    {loading ? (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-28 rounded-lg bg-gray-100 animate-pulse" />
        ))}
      </div>
    ) : items.length === 0 ? (
      <div className="rounded-lg border-2 border-dashed border-gray-200 p-8 text-center bg-white">
        <p className="text-gray-500 mb-3">{emptyLabel}</p>
        {emptyCta && (
          <Link to={emptyCta.to}>
            <Button className="bg-[#4A7C59] hover:bg-[#2F5D3A] text-white">{emptyCta.label}</Button>
          </Link>
        )}
      </div>
    ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>
    )}
  </section>
);

const DashboardCard = ({ title, subtitle, meta, cta, onClick, testId, shipDate, onDelete, secondaryCta, secondaryIcon, onSecondary }) => (
  // iter114f: wrapper changed from <button> → <div role="button"> so we
  // can nest a real <button onClick={onDelete}> inside without the
  // browser flagging "interactive content nested inside a button".
  // Card-click affordance preserved via role + tabIndex + Enter/Space
  // key handler. data-testid stays on the same element so existing
  // tests don't break.
  <div
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick?.();
      }
    }}
    data-testid={testId}
    className="relative bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-lg hover:border-[#6FBF73] transition-all p-5 text-left group cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#6FBF73]/40"
  >
    {onDelete && (
      <button
        type="button"
        onClick={onDelete}
        data-testid={`${testId}-delete`}
        title="Delete this draft"
        aria-label="Delete this draft"
        className="absolute top-2 right-2 p-1.5 rounded-md text-gray-300 hover:text-red-600 hover:bg-red-50 transition-colors z-10"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    )}
    <div className="flex items-start justify-between gap-2 mb-2">
      <p className="font-semibold text-gray-800 truncate flex-1" title={title}>{title}</p>
      {!onDelete && (
        <FileText className="w-4 h-4 text-gray-300 group-hover:text-[#6FBF73] shrink-0" />
      )}
    </div>
    <p className="text-sm text-gray-600 mb-3 line-clamp-1">{subtitle}</p>
    {shipDate && (
      <div className="mb-3">
        <span
          className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs font-medium border border-green-200"
          data-testid={`${testId}-ship-date`}
        >
          <Clock className="w-3 h-3" /> Estimated ship: {shipDate}
        </span>
      </div>
    )}
    <div className="flex items-center justify-between">
      <span className="text-xs text-gray-400">{meta}</span>
      <div className="flex items-center gap-3">
        {onSecondary && (
          <button
            type="button"
            onClick={onSecondary}
            data-testid={`${testId}-reorder`}
            className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-[#4A7C59] hover:bg-[#3D6A4A] px-3 py-1.5 rounded-full transition-colors"
          >
            {secondaryIcon} {secondaryCta}
          </button>
        )}
        <span className="text-xs font-semibold text-[#4A7C59] group-hover:underline">{cta} →</span>
      </div>
    </div>
  </div>
);

export default CustomerDashboard;
